// Map-library-agnostic view constraint logic. Adapters (Google, Leaflet, OL...) supply center/zoom and apply the result.

export interface LatLng {
  lat: number;
  lng: number;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
  minZoom: number;
  /** Used as the snap target when zoomed all the way out and outside bounds. */
  regionCenter?: LatLng;
}

export interface MapView {
  center: LatLng;
  zoom: number;
}

const parseNumbers = (value: string | undefined, count: number) => {
  if (!value) return null;
  const parts = value.split(',').map((p) => parseFloat(p.trim()));
  if (parts.length !== count || parts.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return parts;
};

/**
 * Builds a MapBounds from raw config strings (e.g. env vars). Returns null — i.e. bounds disabled —
 * unless both minZoom and bounds ("north,south,east,west") are present and valid.
 * center is optional ("lat,lng").
 */
export const parseMapBounds = (
  minZoom: string | undefined,
  bounds: string | undefined,
  center?: string
): MapBounds | null => {
  const zoom = parseFloat(minZoom ?? '');
  const edges = parseNumbers(bounds, 4);
  if (!Number.isFinite(zoom) || !edges) {
    if (minZoom || bounds) {
      console.warn('Map bounds config is invalid; bounds disabled.');
    }
    return null;
  }

  const [north, south, east, west] = edges;
  if (north < south || east < west) {
    console.warn('Map bounds require north >= south and east >= west.');
    return null;
  }

  const centerParts = parseNumbers(center, 2);
  return {
    north,
    south,
    east,
    west,
    minZoom: zoom,
    regionCenter: centerParts
      ? { lat: centerParts[0], lng: centerParts[1] }
      : undefined,
  };
};

/** Returns the view the map should move to, or null if the current view is acceptable. */
export const getConstrainedView = (
  view: MapView,
  bounds: MapBounds
): MapView | null => {
  const { lat, lng } = view.center;
  const outside =
    lat > bounds.north ||
    lat < bounds.south ||
    lng > bounds.east ||
    lng < bounds.west;

  if (!outside) {
    return view.zoom < bounds.minZoom
      ? { center: view.center, zoom: bounds.minZoom }
      : null;
  }

  if (view.zoom <= bounds.minZoom && bounds.regionCenter) {
    return { center: bounds.regionCenter, zoom: bounds.minZoom };
  }

  return {
    center: {
      lat: Math.min(bounds.north, Math.max(bounds.south, lat)),
      lng: Math.min(bounds.east, Math.max(bounds.west, lng)),
    },
    zoom: Math.max(view.zoom, bounds.minZoom),
  };
};
